import { useCallback } from 'react';
import type { PlanFeatureInput } from '@frameio/graphql-client/generated';
import {
  useAccountRoleTracking,
  useCurrentPlanTracking,
} from '@frameio/shared/plans/hooks';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { getCookie } from '@frameio/utils/cookies';
import type {
  SubscriptionUpdateSource,
  UsageLimitExceeded,
} from '../../../../feature-gate/config';
import { segmentTrackingValues } from '../../../../feature-gate/config';
import { useCurrentSubscriptionAddOnsTracking } from '../../../../plans/hooks/useCurrentSubscriptionAddOnsTracking';
import { ACCOUNT_CONVERSION_STEPS } from '../constants';

export function useTrackModalOpened() {
  const track = useTrackUnsafe();

  const currentPlanTracking = useCurrentPlanTracking();
  const accountRoleTracking = useAccountRoleTracking();
  const currentSubscriptionAddOnsTracking =
    useCurrentSubscriptionAddOnsTracking();

  const trackModalOpened = useCallback(
    ({
      feature,
      featureGateType,
      firstStep,
      subscriptionUpdateSource,
      usageLimitExceeded,
    }: {
      feature?: PlanFeatureInput;
      featureGateType?: string;
      firstStep: string;
      subscriptionUpdateSource?: SubscriptionUpdateSource;
      usageLimitExceeded?: UsageLimitExceeded;
    }) => {
      switch (firstStep) {
        case ACCOUNT_CONVERSION_STEPS.PLAN_DETAILS: {
          const rapidAuthEnabledCookie = getCookie('rapid_auth_enabled');
          track('upgrade-modal-opened', {
            ...currentPlanTracking,
            ...accountRoleTracking,
            feature: feature
              ? segmentTrackingValues.features[feature]
              : undefined,
            feature_gate_type: featureGateType,
            subscription_update_source: subscriptionUpdateSource,
            usage_limit_exceeded: usageLimitExceeded,
            ...(rapidAuthEnabledCookie && {
              rapid_auth_enabled: rapidAuthEnabledCookie,
            }),
          });
          break;
        }
        case ACCOUNT_CONVERSION_STEPS.PAYMENT:
          track('billing-info-modal-opened', {
            ...currentPlanTracking,
            ...accountRoleTracking,
            subscription_update_source: subscriptionUpdateSource,
            usage_limit_exceeded: usageLimitExceeded,
          });
          break;
        case ACCOUNT_CONVERSION_STEPS.SUMMARY:
          track('summary-modal-opened', {
            ...currentPlanTracking,
            ...accountRoleTracking,
            current_mounted_storage_add_on_count:
              currentSubscriptionAddOnsTracking?.current_mounted_storage_add_on_count ??
              0,
            subscription_update_source: subscriptionUpdateSource,
            usage_limit_exceeded: usageLimitExceeded,
          });
          break;
        default:
          break;
      }
    },
    [
      accountRoleTracking,
      currentPlanTracking,
      currentSubscriptionAddOnsTracking,
      track,
    ]
  );

  return trackModalOpened;
}
